import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Logo, LogoMark, LogoSister, LogoSisterMark } from '../components/icons';
import { useLocation } from '@reach/router';
import StoreContext from '../context/store';
import Fade from 'react-reveal/Fade';
import Link from "../utils/link"
import CollectionSubmenu from '../components/CollectionSubmenu';

// Function to count total quantity
const countQuantity = lineItems => {
  let quantity = 0;
  lineItems.forEach(item => {
    quantity += item.quantity;
  });
  return quantity;
};

const Header = ({ site, handleLinkClick, quoteQuantity, settings }) => {
  const location = useLocation();
  const [scrollDirection, setScrollDirection] = useState('none');
  const [scrolled, setScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [offCanvas, setOffCanvas] = useState(false);
  const [activeParentMenu, setActiveParentMenu] = useState(null); // Track which parent menu is active
  const [openSubMenu, setOpenSubMenu] = useState(null);

  const context = useContext(StoreContext);
  const checkout = context.store ? context.store.checkout : null; // Safely access context.store.checkout
  const [quantity, setQuantity] = useState(countQuantity(checkout ? checkout.lineItems : []));

  // const [quoteQuantity, setQuoteQuantity] = useState(0);

  const siteName = site || 'studio-ashby';

  let lastScrollTop = 0; // Declare the variable using 'let'

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrolled = scrollTop >= 50;
    let direction = 'none';

    if (scrollTop >= 100) {
      if (scrollTop > lastScrollTop) {
        direction = 'down';
      } else {
        direction = 'up';
      }
    }

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

    setScrollDirection(direction);
    setScrolled(scrolled);
  };

  const handleHeaderHover = () => {
    setIsHovered((prevIsHovered) => !prevIsHovered);
    setScrollDirection('none');
  };

  // Toggle off-canvas menu
  const toggleOffCanvas = () => setOffCanvas(prevState => !prevState);

  const hideOffCanvas = () => setOffCanvas(false);

  const normalizePath = path => {
    if (!path) return '';
    return path.endsWith('/') ? path : `${path}/`;
  };

  // Handle link click
  const handleLinkClickHandler = link => {
    // Check if we're navigating to the current page
    if (typeof window !== 'undefined' && normalizePath(link) === normalizePath(location.pathname)) {
      // This prevents the fade-out effect when clicking the same link
      return;
    }
    
    // For different pages, proceed with normal navigation
    handleLinkClick(normalizePath(link));
    hideOffCanvas();
  };

  useEffect(() => {
    let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);


  const headerClass = `header${
    scrollDirection !== 'none' ? ` scrolled-${scrollDirection}` : ''
  }${scrolled ? ' scrolled' : ''}${isHovered ? ' scrolled-up' : ''}${offCanvas ? ' offcanvas-active' : ''}`;

  // Toggle parent menu visibility
  const toggleParentMenu = parentMenu => {
    // handleLinkClick('/sister/collection');
    if (activeParentMenu === parentMenu) {
      setActiveParentMenu(null);
      setOpenSubMenu(null); // Reset sub-menu when switching parent menu
    } else {
      setActiveParentMenu(parentMenu);
      setOpenSubMenu(null); // Reset sub-menu when switching parent menu
    }
  };

  const routeActiveSubMenu = useMemo(() => {
    if (settings?.mainNavigation?.length) {
      const pathMatches = [];

      settings.mainNavigation.forEach(item => {
        const itemKey = item.title.toLowerCase().replace(/\s+/g, '-');

        if (item.hasLink && item.link) {
          pathMatches.push({ path: item.link, itemKey });
        }

        if (item.children && item.children.length) {
          item.children.forEach(child => {
            if (child.hasLink && child.link) {
              pathMatches.push({ path: child.link, itemKey });
            }
          });
        }
      });

      const currentPath = normalizePath(location.pathname);
      const matchingPath = pathMatches
        .filter(({ path }) => {
          const navPath = normalizePath(path);
          return currentPath === navPath || currentPath.startsWith(navPath);
        })
        .sort((a, b) => normalizePath(b.path).length - normalizePath(a.path).length)[0];

      return matchingPath ? matchingPath.itemKey : null;
    }

    const subMenuMapping = {
      '/shop/seating/': 'furniture',
      '/shop/tables/': 'furniture',
      '/shop/lighting/': 'furniture',
      '/shop/cabinets-tables/': 'furniture',
      '/shop/book/': 'accessories',
      '/shop/cushions/': 'accessories',
      '/shop/pattern/': 'fabric',
      '/shop/plain/': 'fabric',
      '/shop/fabric/': 'fabric'
    };

    const currentPath = normalizePath(location.pathname);
    const matchingPath = Object.keys(subMenuMapping)
      .filter(path => currentPath.startsWith(normalizePath(path)))
      .sort((a, b) => normalizePath(b).length - normalizePath(a).length)[0];

    return matchingPath ? subMenuMapping[matchingPath] : null;
  }, [location.pathname, settings?.mainNavigation]);

  const activeSubMenu = openSubMenu?.pathname === location.pathname
    ? openSubMenu.itemKey
    : routeActiveSubMenu;

  const toggleSubMenu = (subMenu) => {
    if (!subMenu) {
      setOpenSubMenu(null);
      return;
    }

    // Clicking the open item closes it. A null itemKey (rather than clearing state)
    // stops the route-derived submenu reopening it until the page changes.
    const itemKey = subMenu === activeSubMenu ? null : subMenu;
    setOpenSubMenu({ itemKey, pathname: location.pathname });
  };

  useEffect(() => {
    setQuantity(countQuantity(checkout ? checkout.lineItems : []));
  }, [checkout]);

  const isActive = (path) => location.pathname === path ? 'active' : '';
  const isActiveJournal = (path) => location.pathname === path ? 'active-journal' : '';

  const isJournalPage = location.pathname.includes('journal');
  const isCollectionPage = location.pathname.includes('/shop/') || 
                         location.pathname.includes('/collection/');


  

  return (
    <>
      {siteName !== 'sister' ? (
        <>
        <header className={`site--${siteName} ${headerClass}`} onMouseEnter={handleHeaderHover} onMouseLeave={handleHeaderHover}>
          <div className="header__inner">
            <div className="header__row">
              <div className="header__col">
                <div className="logo">
                  <Link
                    to='/'
                    onClick={(e) => {
                      e.preventDefault();
                      handleLinkClickHandler('/');
                    }}
                  >
                    <Logo color="#F9F3F0" />
                  </Link>
                </div>
              </div>

              <div className="header__col navigation">
                <nav>
                  <ul>
                    <li className={isActive('/projects/')}>
                      <Link
                        to={'/projects/'}
                       onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/projects/');
                      }}
                      >Projects</Link>
                    </li>
                    <li className={isActive('/about/')}>
                      <Link
                      to={'/about/'}  
                        onClick={(e) => {
                          e.preventDefault();
                          handleLinkClickHandler('/about/');
                        }}
                        >About</Link>
                    </li>
                    <li className={isActive('/contact/')}>
                      <Link 
                      to={'/contact/'}  
                      onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/contact/');
                      }}
                      >Contact</Link>
                    </li>
                    <li className={isActive('/sister/')}>
                      <Link
                      to={'/sister/'}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/sister/');
                      }}
                      >Store</Link>
                    </li>
                  </ul>
                </nav>
              </div>

              <div className="header__col">
                <div className="logo-mark">
                  <Link 
                  to={'/sister/'}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClickHandler('/sister/');
                  }}
                  >
                    <LogoSisterMark color="#F9F3F0" />
                  </Link>
                </div>

                <button onClick={toggleOffCanvas} className={offCanvas ? 'header__hamburger active' : 'header__hamburger'}>
                  <span className="hamburger">
                    <span className='lines'></span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className={`off-canvas ${offCanvas && 'off-canvas--active'}`}>
            <div className='off-canvas__inner'>
                <nav className='off-canvas__nav'>
                  <ul>
                    <li className={isActive('/projects/')}>
                      <Link
                      to={'/projects/'}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/projects/');
                      }}
                      >Projects</Link>
                    </li>
                    <li className={isActive('/about/')}>
                      <Link
                      to={'/about/'}  
                      onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/about/');
                      }}
                      >About</Link>
                    </li>
                    <li className={isActive('/contact/')}>
                      <Link
                      to={'/contact/'}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/contact/');
                      }}
                      
                      >Contact</Link></li>
                      <li className={isActive('/sister/')}>
                      <Link
                      to={'/sister/'}
                       onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/sister/');
                      }}
                      
                      >Store</Link>
                    </li>
                  </ul>
                </nav>
            </div>
            <div className='off-canvas__footer sister'>
              <div className='off-canvas__footer--inner right'>
                <div className="logo-sister">
                  <button onClick={() => handleLinkClickHandler('/sister/')}>
                    <LogoSister color="#F9F3F0" />
                  </button>
                </div>

                <div className='arrow'>
                  <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                    <path d="M9 17L1 9L9 1" stroke="#F9F3F0"/>
                  </svg>
                </div>

              </div>
            </div>

            {isJournalPage && (
              <div className="submenu-container">
                <ul>
                  <li className={isActiveJournal('/sister/journal/')}>
                    <Link to={'/sister/journal/'}>All</Link>
                  </li>
                  {settings?.journalNavigation?.map((item) => (
                    <li key={item._key} className={isActiveJournal(item.link)}>
                      <Link to={item.link}>{item.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* {shouldDisplaySubmenuFabric && (
              <div className='submenu-container'>
                <ul>
                  <li className={`nav-item ${isActive('/shop/pattern/')}`}>
                    <Link to={'/shop/pattern/'}>Pattern</Link>
                  </li>
                  <li className={`nav-item ${isActive('/shop/plain/')}`}>
                    <Link to={'/shop/plain/'}>Plain</Link>
                  </li>
                </ul>
              </div>
            )} */}


{isCollectionPage && (
    <CollectionSubmenu 
      handleLinkClickHandler={handleLinkClickHandler}
      activeSubMenu={activeSubMenu}
      toggleSubMenu={toggleSubMenu}
      navigation={settings?.mainNavigation || []}
    />
  )}

          </div>
        </>
      ) : (
        <>
        <header className={`site--${siteName} ${headerClass}`} onMouseEnter={handleHeaderHover} onMouseLeave={handleHeaderHover}>
          <div className="header__inner">
            <div className="header__row">
              <div className="header__col">
                <div className="logo">
                  <button onClick={() => handleLinkClickHandler('/')}>
                    <LogoMark color="#F9F3F0" />
                  </button>
                </div>
              </div>

              <div className="header__col navigation">
                <nav>
                  <ul>
                    <li className={isActive('/')}>
                      <button onClick={() => handleLinkClickHandler('/')}>Studio</button></li>
                   
                    <li className={isActive('/shop/collection/')}>
                      <Link
                      to={'/shop/collection/'}
                       onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/shop/collection/');
                      }}
                      >Collection</Link>
                    </li>
                    <li className={isActive('/sister/about/')}>

                      <Link
                      to={'/sister/about/'}
                       onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/sister/about/');
                      }}
                      >About</Link>

                    </li>
                    <li className={isActive('/sister/journal/')}>
                      <Link
                      to={'/sister/journal/'}
                       onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/sister/journal/');
                      }}
                      >Journal</Link>

                    </li>
                    <li className={isActive('/sister/contact/')}>
                      
                    <Link
                      to={'/sister/contact/'}
                       onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/sister/contact/');
                      }}
                      >Contact</Link>
                    </li>

                    <li className='navigation-cart'>
                        <button className='cart-btn' aria-label="cart" onClick={() => handleLinkClickHandler('/cart/')}>
                          {quantity > 0 ? (
                            <div className="shopping-bag-quantity">BASKET
                             {/* (<span>{quantity}</span>) */}
                            </div>
                          ) : (
                            <div className="shopping-bag-quantity">BASKET
                            {/* (<span>0</span>) */}
                            </div>
                          )}
                        </button>
                      </li>
                  </ul>
                </nav>
              </div>

              <div className="header__col">
                <div className="logo-mark">
                  <button onClick={() => handleLinkClickHandler('/sister/')}>
                    <LogoSister color="#F9F3F0" />
                  </button>
                </div>

                <div className='header__cart mobile'>
                <Fade delay='850'>
                  <button className='cart-btn' aria-label="cart" onClick={() => handleLinkClickHandler('/cart')}>
                    {quantity > 0 ? (
                      <div className="shopping-bag-quantity">(<span>{quantity}</span>)</div>
                    ) : (
                      <div className="shopping-bag-quantity">(<span>0</span>) </div>
                    )}
                  </button>
                </Fade>
              </div>

                <button onClick={toggleOffCanvas} className={offCanvas ? 'header__hamburger active' : 'header__hamburger'}>
                  <span className="hamburger">
                    <span className='lines'></span>
                  </span>
                </button>
              </div>
            </div>

          </div>
          {isJournalPage && (
            <div className="submenu-container">
              <ul>
                <li className={isActiveJournal('/sister/journal/')}>
                  <Link to={'/sister/journal/'}>All</Link>
                </li>
                {settings?.journalNavigation?.map((item) => (
                  <li key={item._key} className={isActiveJournal(item.link)}>
                    <Link to={item.link}>{item.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}


{isCollectionPage && (
    <CollectionSubmenu 
      handleLinkClickHandler={handleLinkClickHandler}
      activeSubMenu={activeSubMenu}
      toggleSubMenu={toggleSubMenu}
      navigation={settings?.mainNavigation || []}
    />
  )}

        </header>

          <div className={`off-canvas ${offCanvas && 'off-canvas--active'}`}>
            <div className='off-canvas__inner'>
              {/* <button className='off-canvas__menu-close' onClick={hideOffCanvas}>
                <Close color={'#1D1D1B'} />
              </button> */}

                <nav className='off-canvas__nav'>
                  <ul>
                    <li className={isActive('/shop/collection/')}>
                      <Link
                        to={'/shop/collection/'}
                        onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/shop/collection/');
                      }}>
                      Collection
                      </Link>
                      
                      </li>
                    {/* <li className={isActive('/shop/fabric/')}>
                      <Link
                        to={'/shop/fabric'}
                        onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/shop/fabric');
                      }}>
                      Fabric
                      </Link>
                      </li> */}
                    <li className={isActive('/shop/about/')}>
                      <Link
                        to={'/sister/about'}
                        onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/sister/about');
                      }}>
                      About
                      </Link>
                    </li>
                    <li className={isActive('/sister/journal/')}>
                      <Link
                        to={'/sister/journal/'}
                        onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/sister/journal/');
                      }}>
                      Journal
                      </Link>
                    </li>
                    <li className={isActive('/sister/contact/')}>
                      <Link
                        to={'/sister/contact/'}
                        onClick={(e) => {
                        e.preventDefault();
                        handleLinkClickHandler('/sister/contact/');
                      }}>
                      Contact
                      </Link>
                      </li>
                  </ul>
                </nav>
            </div>
            <div className='off-canvas__footer'>
              <div className='off-canvas__footer--inner'>
                <div className='arrow'>
                  <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                    <path d="M9 17L1 9L9 1" stroke="#F9F3F0"/>
                  </svg>
                </div>

                <div className="logo">
                  <button onClick={() => handleLinkClickHandler('/')}>
                    <Logo color="#F9F3F0" />
                  </button>
                </div>

              </div>
            </div>
          </div>

        </>
      )}
    </>
  );
};

export default Header;
